// Make the change
import React, { useRef, useEffect, useState } from 'react';
import mapboxgl, { Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Site } from '../../api/Api';
import MapboxFeature from '../../mapbox-helpers/MapboxFeature';
import { mapBoxConfig } from '../../mapbox-helpers/config';
import { bbox } from '@turf/turf';
import {
  CustomRasterLayer,
  MapLayerInteractiveElement,
} from '../../mapbox-helpers/map-types';
import { addSiteBoundary, fitMapToBoundaries } from 'src/mapbox-helpers/map-layer-utils';
import { IconButton } from '@mui/material';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
// Set Mapbox access token
mapboxgl.accessToken = mapBoxConfig.accessToken;

interface SiteMapWithLayerProps {
  sites: Site[] | undefined;
  hideAllControl?: boolean;
  requiredBounrdaryMarker?: boolean;
  fitToBoundary?: boolean;
  showDevices?: boolean;
  rasterLayer?: CustomRasterLayer[];
}

const SiteMapWithLayer: React.FC<SiteMapWithLayerProps> = ({
  sites,
  fitToBoundary,
  requiredBounrdaryMarker,
  showDevices,
  rasterLayer,
  hideAllControl
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const siteMarkersRef = useRef<Record<string, mapboxgl.Marker | null>>({});
  const [layerToggle, setLayerToggle] = useState<Array<MapLayerInteractiveElement> | undefined>();
  const [mapStyle, setMapStyle] = useState<string>(mapBoxConfig.mapStyle);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    content: string;
    x: number;
    y: number;
  }>({
    visible: false,
    content: '',
    x: 0,
    y: 0,
  });
  const lastViewRef = useRef<{
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
  } | null>(null);

  const isFirstLoadRef = useRef(true);


  const [isSiteListExpanded, setIsSiteListExpanded] = useState(true); // State to manage toggle

  useEffect(() => {
    setLayerToggle([]);
    if (!mapContainer.current || !sites?.length) return;

    const mapInstance = initaliseMap();
    if (!mapInstance) return;

    const handleLoad = () => onMapLoad(mapInstance);
    mapInstance.on('load', handleLoad);

    window.addEventListener('resize', resizeMap);

    return () => {
      window.removeEventListener('resize', resizeMap);

      // Save last view for style changes
      try {
        const center = mapInstance.getCenter();
        const zoom = mapInstance.getZoom();
        const bearing = mapInstance.getBearing();
        const pitch = mapInstance.getPitch();

        lastViewRef.current = {
          center: [center.lng, center.lat],
          zoom,
          bearing,
          pitch,
        };
      } catch {
        // map might already be disposed; ignore
      }

      // Clean up markers
      Object.values(siteMarkersRef.current).forEach((marker) => marker?.remove());
      siteMarkersRef.current = {};

      // Remove the load listener and destroy the map
      mapInstance.off('load', handleLoad);
      mapInstance.remove();

      if (mapRef.current === mapInstance) {
        mapRef.current = null;
      }
    };
  }, [mapStyle, rasterLayer, requiredBounrdaryMarker, showDevices]);



  // ============================ CORE MAP FUNCTIONALITY ===========================
  /**
   * Initializes the Mapbox map.
   * Sets the map style, center, and zoom level based on the provided sites.
   * If no sites are provided, it defaults to a location in India.
   * @returns void
   */
  const initaliseMap = (): Map | null => {
    if (!mapContainer.current) return null;

    const defaultCenter: [number, number] = [
      mapBoxConfig.defaulView.lng,
      mapBoxConfig.defaulView.lat,
    ];
    const defaultZoom = mapBoxConfig.defaulView.zoom;

    const firstSite = sites && sites.length > 0 ? sites[0] : undefined;
    const siteCenter = firstSite?.centroid_wkt
      ? MapboxFeature.parsePointToArray(firstSite.centroid_wkt)
      : defaultCenter;
    const siteZoom = firstSite?.boundary ? 10 : defaultZoom;

    const initialCenter = lastViewRef.current?.center ?? siteCenter;
    const initialZoom = lastViewRef.current?.zoom ?? siteZoom;
    const bearing = lastViewRef.current?.bearing ?? 0;
    const pitch = lastViewRef.current?.pitch ?? 0;

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current as HTMLDivElement,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      center: initialCenter,
      zoom: initialZoom,
      bearing,
      pitch,
    });

    mapRef.current = mapInstance;

    if (!hideAllControl) {
      const fullscreenControl = new mapboxgl.FullscreenControl();
      mapInstance.addControl(fullscreenControl, 'bottom-right');
      mapInstance.addControl(
        new mapboxgl.NavigationControl({ showCompass: true }),
        'bottom-right',
      );
    }

    return mapInstance;
  };


  /**
   * Resizes the map to fit the container.
   * This is useful for ensuring the map displays correctly on window resize.
   * @returns void
   * @see https://docs.mapbox.com/mapbox-gl-js/api/map/#resize
   */
  const resizeMap = () => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  };

  /**
   * Triggers when the map is loaded.
   * Adds controls, layers, and event listeners to the map.
   * It also processes the provided sites to add polygons and markers.
   * If `fitToBoundary` is true, it fits the map to the boundaries of the polygons.
   * @returns void
   * @see https://docs.mapbox.com/mapbox-gl-js/api/map/#on
   */
  const onMapLoad = async (map: Map) => {
    if (!mapContainer.current || !sites?.length) return;

    const newLayerToggleElements: MapLayerInteractiveElement[] = [];
    siteMarkersRef.current = {};

    // 1) Add site boundaries + markers
    sites.forEach((site) => {
      if (site.boundary) {
        addSiteBoundary(map, site, handleMouseEnter, handleMouseLeave, handleMouseMove);
        newLayerToggleElements.push({
          id: `site-boundary-${site.site_id}`,
          opacity: mapBoxConfig.polygon.defaultPolygonOpacity,
          visibility: true,
          name: site.site_name,
          type: 'fill',
        });
      }

      // centroid marker
      if (requiredBounrdaryMarker && site.centroid_wkt) {
        const coordinates = MapboxFeature.parsePointToArray(site.centroid_wkt);
        if (coordinates) {
          const marker = new mapboxgl.Marker()
            .setLngLat(coordinates as [number, number])
            .addTo(map);

          siteMarkersRef.current[site.site_id] = marker;
        }
      }

      // device markers (still commented out in your file)
      // if (showDevices) { ... }
    });

    // 2) Merge into layerToggle (avoid duplicates)
    setLayerToggle((prevLayers) => {
      const combinedLayers = [...(prevLayers || [])];
      newLayerToggleElements.forEach((newLayer) => {
        if (!combinedLayers.some((existingLayer) => existingLayer.id === newLayer.id)) {
          combinedLayers.push(newLayer);
        }
      });
      return combinedLayers;
    });

    // 3) Fit to boundaries (with special handling for preview cards)
    if (fitToBoundary && isFirstLoadRef.current) {
      if (hideAllControl && sites.length === 1) {
        const site = sites[0];
        const { boundary } = site;

        if (boundary) {
          try {
            const tempFeature = new MapboxFeature();
            tempFeature.add(boundary, {});
            const geoJsonBoundary = tempFeature.toJSON();

            if (geoJsonBoundary && geoJsonBoundary.features.length > 0) {
              const bounds = bbox(geoJsonBoundary) as [number, number, number, number];
              const MAX_PREVIEW_ZOOM = 11;

              map.fitBounds(bounds, {
                padding: 20,
                maxZoom: MAX_PREVIEW_ZOOM,
                // animate: false, // uncomment if you want zero animation in cards
              });
            } else {
              fitMapToBoundaries(map, sites, hideAllControl);
            }
          } catch (e) {
            console.error('Error calculating bounding box for preview map:', e);
            fitMapToBoundaries(map, sites, hideAllControl);
          }
        } else {
          fitMapToBoundaries(map, sites, hideAllControl);
        }
      } else {
        fitMapToBoundaries(map, sites, hideAllControl);
      }
    }

    // 4) Add raster layers for this map
    if (rasterLayer?.length) {
      rasterLayer.forEach((layer: CustomRasterLayer) => {
        addRasterLayer(map, layer);
      });
    }

    // 5) Only auto-fit on very first load of this component
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
    }
  };


  const handleMouseEnter = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined; }) => {
    const features = e.features;
    if (features && features.length) {
      const feature = features[0];
      const properties = feature.properties;
      if (properties) {
        const content = `Name: ${properties.Name}<br/>Area: ${properties.Area}`;
        setTooltip({
          visible: true,
          content,
          x: e.point.x,
          y: e.point.y,
        });
      }
    }
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const handleMouseMove = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined; }) => {
    setTooltip((prev) => ({ ...prev, x: e.point.x, y: e.point.y }));
  };

  /**
   * Add raster tile to the Map
   * @param rasterTileData
   * @returns
   */
  const addRasterLayer = (map: Map, layer: CustomRasterLayer) => {
    if (!layer.id || !layer.url) {
      console.warn(
        `Skipping raster layer: id or url_template is missing. Name: ${layer.name || 'Unnamed'}, ID: ${layer.id || 'N/A'}`,
      );
      return;
    }

    const sourceId = `source-raster-${layer.id}`;   // Unique source ID
    const mapboxLayerId = `raster-${layer.id}`;     // Layer ID used in toggles

    // Add source if it doesn't exist
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'raster',
        tiles: [layer.url.split('?')[0]], // GEE URL template
        tileSize: 256,
      } as mapboxgl.RasterSourceSpecification);
    }

    // Add layer if not present
    if (map.getSource(sourceId) && !map.getLayer(mapboxLayerId)) {
      map.addLayer({
        id: mapboxLayerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': mapBoxConfig.raterTile.opacity,
        },
      });
    }

    // Register this layer in the UI controls
    const interactiveLayerConfig: MapLayerInteractiveElement = {
      id: layer.id,
      opacity: mapBoxConfig.raterTile.opacity,
      visibility: true,
      name: layer.name || layer.id,
      type: 'raster',
    };
    pushLayer(interactiveLayerConfig);
  };

  const focusMapOnSingleSite = (site: Site) => {
    if (!mapRef.current || !site) return;
    if (site.boundary) {
      const tempFeature = new MapboxFeature();
      tempFeature.add(site.boundary, {}); // Add the boundary to create a GeoJSON structure
      const geoJsonBoundary = tempFeature.toJSON();

      if (geoJsonBoundary && geoJsonBoundary.features.length > 0) {
        try {
          const calculatedBbox = bbox(geoJsonBoundary);
          mapRef.current.fitBounds(calculatedBbox as [number, number, number, number], {
            padding: 50, // Smaller padding for single site focus
            animate: true,
            duration: 1500,
          });
        } catch (e) {
          console.error("Error calculating bounding box for single site:", e);
          // Fallback to centroid if bbox calculation fails
          if (site.centroid_wkt) {
            const coordinates = MapboxFeature.parsePointToArray(site.centroid_wkt);
            mapRef.current.flyTo({ center: coordinates, zoom: 14, animate: true, duration: 1500 });
          }
        }
      }
    } else if (site.centroid_wkt) {
      const coordinates = MapboxFeature.parsePointToArray(site.centroid_wkt);
      mapRef.current.flyTo({ center: coordinates, zoom: 14, animate: true, duration: 1500 });
    }
  };

  const handleFocusIconClick = (siteId: string) => {
    const actualSiteId = siteId.replace(/^site-boundary-/, ''); // Strip the prefix
    const siteToFocus = sites?.find(s => s.site_id === actualSiteId);
    if (siteToFocus) {
      focusMapOnSingleSite(siteToFocus);
    }
  };
  /**
   * Sets the initial visibility and opacity of the polygons on the map.
   * It initializes the visibility to true and opacity to 1 for all sites.
   * This is called when the map is loaded.
   * @returns void
   */
  const pushLayer = (layer: MapLayerInteractiveElement) => {
    setLayerToggle((prevLayers) => {
      // Prevent adding duplicates to the UI toggle list if an item with the same id already exists
      if (prevLayers && prevLayers.find(l => l.id === layer.id)) {
        return prevLayers;
      }
      return [...(prevLayers || []), layer];
    });
  };

  // ============================ COMPONENT EVENT HANDLERS ===========================
  const handleSiteOpacityChange = (layer: MapLayerInteractiveElement, newOpacity: number) => {
    let mapboxLayerId: string;
    if (layer.type === 'fill') {
      mapboxLayerId = `fill-${layer.id}`;
    } else if (layer.type === 'raster') {
      mapboxLayerId = `raster-${layer.id}`;
    } else {
      mapboxLayerId = `${layer.type}-${layer.id}`; // Fallback for other types
    }
    setLayerToggle((prev: MapLayerInteractiveElement[] | undefined) => {
      return prev?.map((item) => {
        if (item.id === layer.id) {
          return { ...item, opacity: newOpacity }; // <-- fixed
        }
        return item;
      });
    });
    if (mapRef.current && mapRef.current.getLayer(mapboxLayerId)) {
      mapRef.current.setPaintProperty(mapboxLayerId, `${layer.type}-opacity`, newOpacity);
    }
  };

  const setMarkerVisibilityForSite = (siteId: string, isVisible: boolean) => {
    const marker = siteMarkersRef.current[siteId];
    if (!marker) return;
    const el = marker.getElement();
    if (!el) return;
    el.style.display = isVisible ? '' : 'none';
  };


  const toggleLayerVisibility = (layer: MapLayerInteractiveElement) => {
    // Set for Checkbox element
    const isVisible = !layer.visibility;
    setLayerToggle((prev: MapLayerInteractiveElement[] | undefined) => {
      return prev?.map((item) => {
        if (item.id === layer.id) {
          return { ...item, visibility: isVisible };
        }
        return item;
      });
    });

    let mapboxLayerId: string;
    if (layer.type === 'fill') {
      mapboxLayerId = `fill-${layer.id}`;
    } else if (layer.type === 'raster') {
      mapboxLayerId = `raster-${layer.id}`;
    } else {
      mapboxLayerId = `${layer.type}-${layer.id}`; // Fallback for other types
    }
    // update map layer visiblity

    if (mapRef.current && mapRef.current.getLayer(mapboxLayerId)) {
      const visibility = isVisible ? 'visible' : 'none';
      mapRef.current.setLayoutProperty(mapboxLayerId, 'visibility', visibility);

      if (isVisible) {
        // Restore the last opacity when re-enabling
        const lastOpacity = layer.opacity ?? mapBoxConfig.polygon.defaultPolygonOpacity; // Default to 1 if no previous opacity
        handleSiteOpacityChange(layer, lastOpacity);
      }
    } else {
      console.error(`The layer '${mapboxLayerId}' does not exist in the map's style.`);
    }

    if (layer.type === 'fill') {
      const actualSiteId = layer.id.replace(/^site-boundary-/, '');
      setMarkerVisibilityForSite(actualSiteId, isVisible);
    }
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div ref={mapContainer} style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }} />
      {!hideAllControl && (
        <>
          <div className="map-style-menu" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 2, background: 'white', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
            <select onChange={(e) => setMapStyle(e.target.value)} value={mapStyle} id="map-dr">
              <option value="satellite-v9">Satellite</option>
              <option value="streets-v12">Streets</option>
              <option value="light-v11">Light</option>
            </select>
          </div>

          <div
            className="layer-controls"
            style={{
              position: 'absolute',
              top: '65px',
              right: '10px',
              backgroundColor: 'white',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              maxHeight: isSiteListExpanded ? '300px' : '40px',
              overflowY: isSiteListExpanded ? 'auto' : 'hidden',
              transition: 'max-height 0.3s ease',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: isSiteListExpanded ? '10px' : '0',
              }}
            >
              <span>Sites</span>
              <button
                onClick={() => setIsSiteListExpanded((prev) => !prev)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                {isSiteListExpanded ? '-' : '+'}
              </button>
            </div>
            {isSiteListExpanded &&
              layerToggle?.map((layer: MapLayerInteractiveElement) => (
                <div key={layer.name} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={layer.visibility}
                      onChange={() => {
                        toggleLayerVisibility(layer);
                      }}
                    />
                    <label>{layer.name}</label>
                    {layer.type === 'fill' &&
                      <IconButton aria-label="Focus on this site" disabled={!layer.visibility} onClick={() => handleFocusIconClick(layer.id)} >
                        <CenterFocusWeakIcon />
                      </IconButton>
                    }
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.2"
                    value={layer.opacity ?? mapBoxConfig.polygon.defaultPolygonOpacity}
                    onChange={(e) => handleSiteOpacityChange(layer, parseFloat(e.target.value))}
                    style={{ width: '100%' }}
                    disabled={!layer.visibility}
                  />
                </div>
              ))}
          </div>
        </>
      )}
      {/* Tooltip */}
      {tooltip.visible && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            backgroundColor: 'white',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 2,
            pointerEvents: 'none', // Makes sure tooltip does not interfere with other map elements
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
    </div>
  );
};

export default SiteMapWithLayer;
